import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSites, useScoreSite } from '@/hooks';
import { useAuthStore } from '@/store/auth';
import { ThreatTierBadge } from '@/components/ThreatTierBadge';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate, formatCurrency } from '@/utils/format';
import type { SiteFilters } from '@/api/sites';

export function Sites() {
  const [filters, setFilters] = useState<SiteFilters>({
    page: 1,
    page_size: 20,
  });
  const [search, setSearch] = useState('');

  const { products } = useAuthStore();
  const hasPolygon = products.includes('polygon');

  const { data, isLoading } = useSites({ ...filters, search: search || undefined });
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/data-vault') ? '/data-vault/sites' : '/sites';
  const scoreMutation = useScoreSite();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
  };

  const handleScoreSite = async (siteId: string) => {
    await scoreMutation.mutateAsync(siteId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b border-mono-800 pb-4">
        <h1 className="text-lg font-medium text-mono-100 tracking-widest">SITES</h1>
        <Link
          to={`${basePath}/new`}
          className="flex items-center gap-2 px-3 py-1.5 border border-mono-100 bg-mono-100 text-mono-950 text-xs tracking-widest hover:bg-mono-200 transition-colors"
        >
          <PlusIcon className="h-3 w-3" />
          ADD
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mono-500" />
            <input
              type="search"
              placeholder="SEARCH..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-xs tracking-wider focus:border-mono-500 focus:outline-none placeholder-mono-600"
            />
          </div>
        </form>
        {hasPolygon && (
          <select
            value={filters.status || ''}
            onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined, page: 1 })}
            className="px-3 py-2 bg-mono-900 border border-mono-700 text-mono-300 text-xs tracking-wider focus:border-mono-500 focus:outline-none"
          >
            <option value="">ALL STATUS</option>
            <option value="active">ACTIVE</option>
            <option value="monitoring">MONITORING</option>
            <option value="remediation">REMEDIATION</option>
            <option value="closure_pending">CLOSURE PENDING</option>
            <option value="closed">CLOSED</option>
          </select>
        )}
        {hasPolygon && (
          <select
            value={filters.threat_tier || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                threat_tier: e.target.value ? parseInt(e.target.value) : undefined,
                page: 1,
              })
            }
            className="px-3 py-2 bg-mono-900 border border-mono-700 text-mono-300 text-xs tracking-wider focus:border-mono-500 focus:outline-none"
          >
            <option value="">ALL TIERS</option>
            <option value="1">T1 CRITICAL</option>
            <option value="2">T2 HIGH</option>
            <option value="3">T3 MODERATE</option>
            <option value="4">T4 LOW</option>
            <option value="5">T5 MINIMAL</option>
          </select>
        )}
      </div>

      {/* Sites table */}
      <div className="border border-mono-800 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-mono-800 bg-mono-900">
              <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">SITE</th>
              <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">LOCATION</th>
              <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">STATUS</th>
              {hasPolygon && <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">TIER</th>}
              {hasPolygon && <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">LIABILITY</th>}
              {hasPolygon && <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">SCORED</th>}
              {hasPolygon && <th className="px-4 py-3 text-right text-[10px] text-mono-500 tracking-widest font-medium"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-mono-800">
            {isLoading ? (
              <tr>
                <td colSpan={hasPolygon ? 7 : 3} className="px-4 py-8 text-center text-mono-500 text-xs tracking-wider">
                  LOADING...
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td colSpan={hasPolygon ? 7 : 3} className="px-4 py-8 text-center text-mono-500 text-xs tracking-wider">
                  NO SITES FOUND
                </td>
              </tr>
            ) : (
              data?.items.map((site) => (
                <tr key={site.id} className="hover:bg-mono-900/50 cursor-pointer" onClick={() => navigate(`${basePath}/${site.id}`)}>
                  <td className="px-4 py-3">
                    <Link to={`${basePath}/${site.id}`} className="text-sm text-mono-200 hover:text-mono-100">
                      {site.name}
                    </Link>
                    {site.site_identifier && (
                      <p className="text-[10px] text-mono-500 tracking-wider">{site.site_identifier}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-mono-400">
                    {site.city}, {site.state}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={site.status} size="sm" />
                  </td>
                  {hasPolygon && (
                    <td className="px-4 py-3">
                      {site.latest_score ? (
                        <ThreatTierBadge tier={site.latest_score.threat_tier} size="sm" showLabel={false} />
                      ) : (
                        <span className="text-[10px] text-mono-600 tracking-wider">--</span>
                      )}
                    </td>
                  )}
                  {hasPolygon && (
                    <td className="px-4 py-3 text-xs text-mono-400">
                      {formatCurrency(site.latest_score?.estimated_cost_to_closure)}
                    </td>
                  )}
                  {hasPolygon && (
                    <td className="px-4 py-3 text-xs text-mono-500">
                      {site.latest_score ? formatDate(site.latest_score.scored_at) : '--'}
                    </td>
                  )}
                  {hasPolygon && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleScoreSite(site.id); }}
                        disabled={scoreMutation.isPending}
                        className="text-[10px] text-mono-400 hover:text-mono-200 tracking-widest disabled:opacity-50"
                      >
                        {scoreMutation.isPending ? 'SCORING...' : 'SCORE'}
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-between border-t border-mono-800 pt-4">
          <p className="text-[10px] text-mono-500 tracking-wider">
            PAGE {data.page} OF {data.pages} ({data.total} SITES)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
              disabled={data.page <= 1}
              className="px-3 py-1.5 border border-mono-700 text-mono-400 text-[10px] tracking-widest hover:border-mono-500 disabled:opacity-50 disabled:hover:border-mono-700"
            >
              PREV
            </button>
            <button
              onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
              disabled={data.page >= data.pages}
              className="px-3 py-1.5 border border-mono-700 text-mono-400 text-[10px] tracking-widest hover:border-mono-500 disabled:opacity-50 disabled:hover:border-mono-700"
            >
              NEXT
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
